import React from 'react';
import { Button, Typography, TextField } from '@material-ui/core';
import { css } from '@emotion/core';
import { JsonEditor as Editor } from 'jsoneditor-react';
import { useHistory } from 'react-router-dom';
import { Formik } from 'formik';
import { useRootStore } from '../../../stores/BridgeProvider';
import { FlexDiv, WideBox, StyledForm, ButtonFlex } from '../../styles/styles';
import 'jsoneditor-react/es/editor.min.css';

const DT_JSON = {
  items: [],
  template: {
    Deals: [
      {
        Title: '$Title',
        DealNumber: 'AUTO-GENERATE',
        AgentContactName: '$AgentContactName',
        Description: '$Description',
        ContractDate: '%CurrentDate',
        TransactionDate: '$TransactionDate',
        BillableDate: '$TransactionDate',
        PayableDate: '$TransactionDate',
        StatusName: '$StatusName',
      },
    ],
    DealTransactions: [],
  },
};

const EditorStyles = css`
  .jsoneditor {
    min-height: 600px !important;
    textarea {
      min-height: 600px;
    }
  }
  #JSONEditorContainer {
    width: 100%;
    margin-top: 1rem;
  }
`;

const spacer = css`
  margin-left: 1.2rem;
`;

const NewDealTemplate = () => {
  const { authStore } = useRootStore();
  const history = useHistory();

  return (
    <FlexDiv>
      <WideBox css={EditorStyles}>
        <Formik
          initialValues={{
            template: DT_JSON,
            name: '',
            description: '',
          }}
          onSubmit={async (
            { template, name, description },
            { setSubmitting },
          ) => {
            try {
              const DT_FORMATTED = JSON.stringify(template);
              const { data } = await authStore.request.post(
                `${process.env.REACT_APP_API}/api/deal-template`,
                {
                  Template: DT_FORMATTED,
                  Name: name,
                  Description: description,
                },
              );
              history.push(`/deal-template/${data.id}`);
            } catch (e) {
              console.error(e);
            }
            setSubmitting(false);
          }}
          render={formikProps => {
            const {
              handleSubmit,
              isSubmitting,
              handleChange,
              handleBlur,
              values,
              setFieldValue,
              isValid,
            } = formikProps;
            return (
              <StyledForm onSubmit={handleSubmit}>
                <Typography variant="h2" gutterBottom>
                  New Custom Template
                </Typography>
                <TextField
                  label="Template Name"
                  name="name"
                  margin="normal"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  variant="outlined"
                  required
                  fullWidth
                />
                <TextField
                  label="Description"
                  name="description"
                  margin="normal"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.description}
                  variant="outlined"
                  fullWidth
                />
                <ButtonFlex>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    size="large"
                    style={{ marginTop: '1.2rem', marginBottom: '1.2rem' }}
                    disabled={isSubmitting || !isValid}
                  >
                    Continue
                  </Button>
                  <Button
                    color="secondary"
                    variant="outlined"
                    size="large"
                    style={{
                      marginTop: '1.2rem',
                      marginBottom: '1.2rem',
                    }}
                    onClick={() => history.goBack()}
                  >
                    Cancel
                  </Button>
                </ButtonFlex>
              </StyledForm>
            );
          }}
        />
      </WideBox>
      <WideBox style={{ textAlign: 'left', minHeight: 'auto' }}>
        <Typography
          variant="h4"
          style={{
            textAlign: 'center',
            marginBottom: '2rem',
          }}
        >
          Template Requirements:
        </Typography>
        <Typography variant="subtitle1">
          <b>items:</b> Array of items to be displayed as inputs in deal
          creation from template
        </Typography>
        <div css={spacer}>
          <span>[</span>
          <div css={spacer}>
            <span>&#123;</span>
            <div css={spacer}>
              <Typography variant="subtitle1">
                <b>name:</b> Input name
              </Typography>
              <Typography variant="subtitle1">
                <b>type:</b> One of ["string", "contact", "agent", "dollar",
                "date", "enum"]
              </Typography>
              <Typography variant="subtitle1">
                <b>required:</b> One of ["true", "false"]
              </Typography>
              <Typography variant="subtitle1">
                <b>displayName:</b> Input label
              </Typography>
            </div>
            <span>&#125;</span>
            <br />
            <span>...</span>
          </div>
          <span>]</span>
        </div>
        <Typography variant="subtitle1">
          <b>template:</b> Object that creates the template
        </Typography>
        <div css={spacer}>
          <span>&#123;</span>
          <div css={spacer}>
            <Typography variant="subtitle1">
              <b>Deals:</b> Array of deals to be created
            </Typography>
            <div css={spacer}>
              <span>[</span>
              <div css={spacer}>
                <span>&#123;</span>
                <div css={spacer}>
                  <Typography variant="subtitle1">
                    <b>Title:</b> String
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>Description:</b> String
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>ContractDate:</b> Date
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>TransactionDate:</b> Date
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>BillableDate:</b> Date
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>PayableDate:</b> Date
                  </Typography>
                  <Typography variant="subtitle1">
                    <b>StatusId:</b> Integer
                  </Typography>
                </div>
                <span>&#125;</span>
              </div>
              <span>]</span>
            </div>
          </div>
          <div css={spacer}>
            <Typography variant="subtitle1">
              <b>DealTransactions:</b> Array of deal transactions to be created
            </Typography>
            <div css={spacer}>
              <span>[</span>
              <div css={spacer}>
                <div css={spacer}>
                  <span>&#123;</span>
                  <div css={spacer}>
                    <Typography variant="subtitle1">
                      <b>Amount:</b> Number
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>TranType:</b> One of ["AR", "AP"]
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>TranCategoryCode:</b> String
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>ContactName:</b> String
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>Quantity:</b> Integer
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>Description:</b> String
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>DebitAccountCode:</b> String, Null will default to
                      account on Transaction category
                    </Typography>
                    <Typography variant="subtitle1">
                      <b>CreditAccountCode:</b> String, Null will default to
                      account on Transaction category
                    </Typography>
                  </div>
                  <span>&#125;</span>
                  <br />
                  <span>...</span>
                </div>
              </div>
              <span>]</span>
            </div>
          </div>
          <span>&#125;</span>
        </div>
      </WideBox>
      <WideBox style={{ textAlign: 'left', minHeight: 'auto' }}>
        <Typography
          variant="h4"
          style={{
            textAlign: 'center',
            marginBottom: '2rem',
          }}
        >
          Placeholder Key:
        </Typography>
        <Typography variant="subtitle1">
          <b>$"someVar":</b> Value set to input value of "someVar"
        </Typography>
        <Typography variant="subtitle1">
          <b>%CurrentDate:</b> Value set to current date
        </Typography>
      </WideBox>
    </FlexDiv>
  );
};

export default NewDealTemplate;

//  <div id="JSONEditorContainer">
//    <Typography
//      variant="subtitle1"
//      style={{
//        marginBottom: '0.5rem',
//        textAlign: 'left',
//      }}
//    >
//      Template JSON:
//    </Typography>
//    <Editor
//      value={DT_JSON}
//      onChange={v => setFieldValue('template', v)}
//      mode="code"
//      allowedModes={['code', 'tree']}
//    />
//  </div>;

// const DT_JSON = {
//   items: [
//     {
//       name: 'Title',
//       type: 'string',
//       required: true,
//       displayName: 'Deal Name',
//     },
//     {
//       name: 'Description',
//       type: 'string',
//       required: false,
//       displayName: 'Description',
//     },
//     {
//       name: 'AgentContactName',
//       type: 'agent',
//       required: true,
//       displayName: 'Agent',
//     },
//     {
//       name: 'TenantName',
//       type: 'contact',
//       required: true,
//       displayName: 'Tenant',
//     },
//     {
//       name: 'LandlordName',
//       type: 'contact',
//       required: true,
//       displayName: 'Landlord',
//     },
//     {
//       name: 'FMR',
//       type: 'dollar',
//       required: true,
//       displayName: 'FMR Amount',
//     },
//     {
//       name: 'LMR',
//       type: 'dollar',
//       required: true,
//       displayName: 'LMR Amount',
//     },
//     {
//       name: 'SEC',
//       type: 'dollar',
//       required: true,
//       displayName: 'Security Fee',
//     },
//     {
//       name: 'TransactionDate',
//       type: 'date',
//       required: true,
//       displayName: 'Transaction Date',
//     },
//     {
//       name: 'StatusName',
//       type: 'enum',
//       required: true,
//       displayName: 'Deal Status',
//       enumOptions: [
//         'Draft',
//         'Submitted',
//         'Approved',
//         'Closed',
//         'Void',
//         'Deleted',
//       ],
//     },
//   ],
//   template: {
//     Deals: [
//       {
//         Title: '$Title',
//         DealNumber: 'AUTO-GENERATE',
//         AgentContactName: '$AgentContactName',
//         Description: '$Description',
//         ContractDate: '%CurrentDate',
//         TransactionDate: '$TransactionDate',
//         BillableDate: '$TransactionDate',
//         PayableDate: '$TransactionDate',
//         StatusName: '$StatusName',
//       },
//     ],
//     DealTransactions: [
//       {
//         DealTitle: '$Title',
//         Amount: '$FMR',
//         TranType: 'AR',
//         TranCategoryCode: 'FMR',
//         ContactName: '$TenantName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$FMR',
//         TranType: 'AP',
//         TranCategoryCode: 'FMR',
//         ContactName: '$LandlordName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$LMR',
//         TranType: 'AR',
//         TranCategoryCode: 'LMR',
//         ContactName: '$TenantName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$LMR',
//         TranType: 'AP',
//         TranCategoryCode: 'LMR',
//         ContactName: '$LandlordName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$SEC',
//         TranType: 'AR',
//         TranCategoryCode: 'SEC',
//         ContactName: '$TenantName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$SEC',
//         TranType: 'AP',
//         TranCategoryCode: 'SEC',
//         ContactName: '$LandlordName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$FMR',
//         TranType: 'AR',
//         TranCategoryCode: 'RCOM',
//         ContactName: '$TenantName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//       {
//         DealTitle: '$Title',
//         Amount: '$FMR * 0.5',
//         TranType: 'AP',
//         TranCategoryCode: 'RCOM',
//         ContactName: '$AgentContactName',
//         Quantity: 1,
//         Description: '$Description',
//       },
//     ],
//   },
// };
